import React, { useEffect, useMemo } from 'react';
import { useAuth } from 'wasp/client/auth';
import CommonLayout from '../../../components/layout/CommonLayout';
import { dashboardLayoutConfig } from './dashboardConfig';
import { useDashboardLayout } from '../../contexts/DashboardLayoutContext';
import { useTopBarContent } from '../../contexts/TopBarContentContext';

interface DashboardLayoutProps {
  children: React.ReactNode;
  showViewSwitcher?: boolean;
  activeView?: 'cards' | 'list' | 'table';
  onViewChange?: (view: 'cards' | 'list' | 'table') => void;
  forceSidebarCollapse?: boolean;
  customTopBarContent?: React.ReactNode;
  customSidebarContent?: React.ReactNode;
}

export default function DashboardLayout({
  children,
  showViewSwitcher = false,
  activeView = 'cards',
  onViewChange,
  forceSidebarCollapse = false,
  customTopBarContent,
  customSidebarContent,
}: DashboardLayoutProps) {
  const { data: user } = useAuth();
  const { isShellActive, setLayoutState, resetLayoutState } = useDashboardLayout();
  const { setTopBarContent, clearTopBarContent } = useTopBarContent();

  // Memoized config for standalone fallback (must be before any early returns)
  const standaloneConfig = useMemo(() => ({
    ...dashboardLayoutConfig,
    customTopBarContent,
    customSidebarContent,
  }), [customTopBarContent, customSidebarContent]);

  // When the persistent shell in App.tsx is active, sync non-JSX layout props upward
  useEffect(() => {
    if (!isShellActive) return;
    setLayoutState({ showViewSwitcher, activeView, onViewChange, forceSidebarCollapse });
    return () => resetLayoutState();
    // onViewChange excluded — new function ref on every render causes infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShellActive, showViewSwitcher, activeView, forceSidebarCollapse]);

  // Sync custom top bar content via its own context
  useEffect(() => {
    if (!isShellActive) return;
    if (customTopBarContent !== undefined) {
      setTopBarContent(customTopBarContent);
      return () => clearTopBarContent();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShellActive, customTopBarContent]);

  if (isShellActive) {
    // Shell already renders the sidebar — just pass through content
    return <>{children}</>;
  }

  // Fallback: standalone layout (used when shell is not active, e.g. some admin org content pages)
  return (
    <CommonLayout
      config={standaloneConfig}
      showViewSwitcher={showViewSwitcher}
      activeView={activeView}
      onViewChange={onViewChange}
      forceSidebarCollapse={forceSidebarCollapse}
      user={user}
    >
      {children}
    </CommonLayout>
  );
}



